import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  makeStyles,
  createStyles,
  useTheme,
  Grid,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";
import _ from "lodash";
import { secondaryDb } from "../../../../utils/firebase/temp";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
export interface BoxDetailsProps {
  widgetData: any;
  configDetails: any;
  clientId: any;
  fontScale?: number;
}
const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    rootBoxWarning: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.yellow.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    rootBoxNormal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.green.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    rootBoxCritical: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.custom.red.light,
      borderRadius: 10,
      padding: "0px 20px",
      height: "100%",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    highlightedNormal: {
      color: theme.palette.custom.green.light,
      transition: "color 1s ease", // fades back to normal
    },
    colorWhite: {
      color: "white",
    },
    highlightedCritical: {
      color: theme.palette.custom.red.light,
      transition: "color 1s ease", // fades back to normal
    },
    highlightedCriticalText: {
      color: theme.palette.custom.red.light,
    },
    highlightedWarning: {
      color: theme.palette.custom.yellow.light,
      transition: "color 1s ease", // fades back to normal
    },
    highlightedWarningText: {
      color: theme.palette.custom.yellow.light,
    },
    normal: {
      color: theme.palette.custom.orange.contrastText,
      transition: "color 1s ease",
    },
    colorBlack: {
      color: "black",
    },
  }),
);
function getAverageTimeFormatted(totalSeconds: any, count: any) {
  if (count === 0) return "0 sec";
  const avgSeconds = Math.floor(totalSeconds / count);
  const minutes = Math.floor(avgSeconds / 60);
  const seconds = avgSeconds % 60;
  if (minutes > 0 && seconds > 0) {
    return `${minutes} min ${seconds} sec`;
  } else if (minutes > 0) {
    return `${minutes} min`;
  } else {
    return `${seconds} sec`;
  }
}
const SingleWidgetBox: React.FunctionComponent<BoxDetailsProps> = ({
  widgetData,
  configDetails,
  clientId,
  fontScale,
}) => {
  const classes = useStyles();
  const [highlighted, setHighlighted] = useState(false);
  const [criticalType, setCriticalType] = useState("");
  const [warningType, setWarningType] = useState("");
  const [prevValue, setPrevValue] = useState<any>({});
  const [error, setError] = useState("");
  const [locationMetricsNode, setLocationMetricsNode] = useState<any>({});
  const [stationMetricsNode, setStationMetricsNode] = useState<any>({});
  const [value, setValue] = useState<any>({});
  const previousValueRef = useRef<any>(null);
  const isFirstRender = useRef(true);
  const getValueByPath = (obj: any, path: string[]) => {
    return path.reduce((acc, key) => acc && acc[key], obj);
  };
  useEffect(() => {
    if (
      !clientId ||
      !widgetData ||
      (widgetData?.path[0] !== "locationAverageTime" &&
        widgetData?.path[0] !== "preparationAverageTime" &&
        widgetData?.path[0] !== "deliveryAverageTime" &&
        widgetData?.path[0] !== "loadAverageTime")
    ) {
      return; // 🔥 Do nothing if clientId is not ready
    }
    // Fetch data for locationMetrics
    const locationMetricsRef = secondaryDb.ref(
      `locationMetrics/${widgetData?.clientId || clientId}/${
        widgetData?.path[0]
      }`,
    );
    locationMetricsRef.on(
      "value",
      (snapshot) => {
        if (snapshot.exists()) {
          setLocationMetricsNode(snapshot.val());
        } else {
          setLocationMetricsNode({});
        }
      },
      (error: any) => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      },
    );
    return () => {
      locationMetricsRef.off(); // Cleanup listener for locationMetrics
    };
  }, [clientId, widgetData?.key]);
  useEffect(() => {
    if (
      !clientId ||
      !widgetData ||
      widgetData?.path[0] !== "stationAverageTime"
    ) {
      return; // 🔥 Do nothing if clientId is not ready
    }
    // Fetch data for stationMetrics
    const stationMetricsRef = secondaryDb.ref(
      `stationMetrics/${widgetData?.clientId || clientId}`,
    );
    stationMetricsRef.on(
      "value",
      (snapshot) => {
        if (snapshot.exists()) {
          setStationMetricsNode(snapshot.val());
        } else {
          setStationMetricsNode({});
        }
      },
      (error: any) => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      },
    );
    return () => {
      stationMetricsRef.off(); // Cleanup listener for stationMetrics
    };
  }, [clientId, widgetData?.key]);
  useEffect(() => {
    if (widgetData?.path[0] === "locationAverageTime") {
      const value = getValueByPath(
        { locationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "deliveryAverageTime") {
      const value = getValueByPath(
        { deliveryAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "preparationAverageTime") {
      const value = getValueByPath(
        { preparationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else if (widgetData?.path[0] === "loadAverageTime") {
      const value = getValueByPath(
        { loadAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    } else {
      const value = getValueByPath(
        widgetData?.path[0] === "stationAverageTime"
          ? { stationAverageTime: stationMetricsNode }
          : { locationAverageTime: locationMetricsNode },
        widgetData?.path,
      );
      setValue(value);
    }
  }, [stationMetricsNode, locationMetricsNode, widgetData?.path]);
  useEffect(() => {
    const { criticalType, warningType } = configDetails;
    setCriticalType(criticalType);
    setWarningType(warningType);
  }, [configDetails]);
  useEffect(() => {
    if (isFirstRender.current) {
      setPrevValue(value);
      isFirstRender.current = false;
    } else if (
      JSON.stringify(previousValueRef.current) !== JSON.stringify(value)
    ) {
      setHighlighted(true);
      const holdTimer = setTimeout(() => {
        const fadeBackTimer = setTimeout(() => {
          setHighlighted(false);
          setPrevValue(value);
        }, 1000); // Fade duration: 1 second
        return () => clearTimeout(fadeBackTimer);
      }, 3000); // Hold green for 3 seconds
      return () => clearTimeout(holdTimer);
    }
    previousValueRef.current = value;
  }, [value]);
  useEffect(() => {
    const { criticalType, warningType } = configDetails;
    setCriticalType(criticalType);
    setWarningType(warningType);
  }, [configDetails]);
  useEffect(() => {
    if (isFirstRender.current) {
      setPrevValue(value);
      isFirstRender.current = false;
    } else if (
      JSON.stringify(previousValueRef.current) !== JSON.stringify(value)
    ) {
      setHighlighted(true);
      const holdTimer = setTimeout(() => {
        const fadeBackTimer = setTimeout(() => {
          setHighlighted(false);
          setPrevValue(value);
        }, 1000); // Fade duration: 1 second
        return () => clearTimeout(fadeBackTimer);
      }, 3000); // Hold green for 3 seconds
      return () => clearTimeout(holdTimer);
    }
    previousValueRef.current = value;
  }, [value]);
  const prevValueRef = useRef(null);
  const [valueChanged, setValueChanged] = useState(false);
  useEffect(() => {
    if (!highlighted && prevValueRef.current !== value) {
      setValueChanged(true);
      // Reset after 1 second so it only flashes the new color briefly
    }
    if (
      (criticalType === "blink" && value?.status === "critical") ||
      (warningType === "blink" && value?.status === "warning") ||
      value?.status === "normal"
    ) {
      const fadeBackTimer = setTimeout(() => {
        setValueChanged(false);
      }, 3000); // 1s fade duration
      return () => clearTimeout(fadeBackTimer);
    }
    prevValueRef.current = value;
  }, [value, highlighted, criticalType, warningType]);
  const handleClassName = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.colorWhite;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.colorBlack;
      } else if (value?.status === "critical") {
        return criticalType === "text"
          ? classes.highlightedCriticalText
          : classes.highlightedCritical;
      } else if (value?.status === "warning") {
        return warningType === "text"
          ? classes.highlightedWarningText
          : classes.highlightedWarning;
      } else if (value?.status === "normal") {
        return classes.highlightedNormal;
      } else {
        return classes.normal;
      }
    }
  };
  const handleBoxColor = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.rootBoxCritical;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.rootBoxWarning;
      } else if (warningType === "box") {
        return classes.root;
      } else {
        return classes.root; // fallback
      }
    } else {
      return classes.root;
    }
  };
  const handleClassNameIcon = () => {
    if (valueChanged || highlighted) {
      if (value?.status === "critical" && criticalType === "box") {
        return classes.colorWhite;
      } else if (value?.status === "warning" && warningType === "box") {
        return classes.colorBlack;
      } else {
        return classes.normal;
      }
    }
  };
  const theme: CustomTheme = useTheme();
  return (
    <div className={handleBoxColor()}>
      <Grid container>
        <Grid item xs={12}>
          {getAverageTimeFormatted(
            prevValue?.["totalTime"] ? prevValue?.["totalTime"] : 0,
            prevValue?.["count"] ? prevValue?.["count"] : 0,
          ) !==
          getAverageTimeFormatted(
            value?.["totalTime"] ? value?.["totalTime"] : 0,
            value?.["count"] ? value?.["count"] : 0,
          ) ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  widgetData?.["align"] === "left"
                    ? "start"
                    : widgetData?.["align"] === "right"
                    ? "end"
                    : "center",
              }}
            >
              <Typography
                variant="h4"
                align={widgetData["align"] ? widgetData["align"] : "center"}
                style={{
                  fontSize: `${
                    (widgetData["averageFontSize"] ?? 28) * (fontScale ?? 1)
                  }px`,
                }}
                className={handleClassName()}
              >
                {value
                  ? getAverageTimeFormatted(
                      value["totalTime"] || 0,
                      value["count"] || 0,
                    )
                  : "0 sec"}
              </Typography>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  widgetData?.["align"] === "left"
                    ? "start"
                    : widgetData?.["align"] === "right"
                    ? "end"
                    : "center",
              }}
            >
              <Typography
                variant="h4"
                align={widgetData["align"] ? widgetData["align"] : "center"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: `${
                    (widgetData["averageFontSize"] ?? 28) * (fontScale ?? 1)
                  }px`,
                }}
                className={handleClassName()}
              >
                {value
                  ? getAverageTimeFormatted(
                      value["totalTime"] || 0,
                      value["count"] || 0,
                    )
                  : "0 sec"}
              </Typography>
            </div>
          )}
        </Grid>
        <Grid item xs={12} style={{ marginTop: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                widgetData?.["align"] === "left"
                  ? "start"
                  : widgetData?.["align"] === "right"
                  ? "end"
                  : "center",
              width: "100%",
              zIndex: 0,
            }}
          >
            <Typography
              align="left"
              className={handleClassNameIcon()}
              style={{
                fontSize: `${
                  (widgetData["titleFontSize"] ?? 12) * (fontScale ?? 1)
                }px`,
              }}
            >
              {widgetData.label}
            </Typography>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};
export default SingleWidgetBox;
